import { config } from "../config";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const billingAdminBaseUrl = `${protocol}://${host}/clinics/v1/billing/admin`;

export type TBillingAdminTotals = {
  total_clinics: number;
  free: number;
  paid: number;
  delinquent: number;
};

export type TBillingAdminSubscription = {
  id: string;
  status?: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
};

export type TBillingAdminInvoice = {
  id: string;
  amount_paid?: number | null;
  currency?: string | null;
  status?: string | null;
  hosted_invoice_url?: string | null;
  created_at?: string | null;
  next_payment_attempt?: number | null;
};

export type TBillingAdminClinicSummary = {
  clinic_uuid: string;
  clinic_name: string;
  created_at?: string | null;
  clinic_created_at?: string | null;
  current_plan?: string | null;
  plan_type?: string | null;
  is_delinquent: boolean;
  remaining_free_simulation_count?: number | null;
  latest_invoice?: TBillingAdminInvoice | null;
};

export type TBillingAdminSummaryResponse = TBillingAdminTotals & {
  by_plan_type?: Record<string, number>;
  clinics: TBillingAdminClinicSummary[];
};

export type TBillingAdminClinicHistoryResponse = {
  clinic_uuid: string;
  clinic_name?: string | null;
  is_delinquent?: boolean;
  subscriptions: TBillingAdminSubscription[];
  invoices: TBillingAdminInvoice[];
};

export type TTransformsUsageGranularity =
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly";

export type TTransformMetricsBucket = {
  period_start: string;
  simulations: number;
  transform_images: number;
};

export type TTransformMetricsSeries = Record<
  TTransformsUsageGranularity,
  TTransformMetricsBucket[]
>;

export type TClinicTransformMetrics = {
  clinic_uuid: string;
  clinic_name?: string | null;
  gross_simulations: number;
  gross_transform_images: number;
  series: TTransformMetricsSeries;
};

type TTransformsUsageMetricsBaseResponse = {
  start_date: string;
  end_date: string;
  gross_simulations: number;
  gross_transform_images: number;
  series: TTransformMetricsSeries;
};

export type TTransformsUsageMetricsResponse =
  TTransformsUsageMetricsBaseResponse & {
    clinics: TClinicTransformMetrics[];
  };

export type TProcedureComboTransformMetrics = {
  procedure_types: string[];
  procedure_group_key: string;
  gross_simulations: number;
  gross_transform_images: number;
  series: TTransformMetricsSeries;
};

export type TTransformsUsageMetricsByProcedureResponse =
  TTransformsUsageMetricsBaseResponse & {
    procedure_groups: TProcedureComboTransformMetrics[];
  };

export type TAudioNotesSeriesBucket = {
  period_start: string;
  audio_total: number;
  audio_uploaded: number;
  audio_not_uploaded: number;
  notes_by_type: Record<string, number>;
  audio_by_upload_mode?: Record<string, number>;
  audio_with_upload_error?: number;
  audio_note_attachment_breakdown?: TAudioNoteAttachmentBreakdown;
};

export type TImagesUploadedSeriesBucket = {
  period_start: string;
  images_by_type: Record<string, number>;
};

export type TAudioNoteAttachmentBreakdown = {
  notes_0: number;
  notes_1: number;
  notes_2: number;
  notes_3_plus: number;
};

type TAudioNotesUsageMetricsBaseResponse = {
  start_date: string;
  end_date: string;
  gross_audio_total: number;
  gross_audio_uploaded: number;
  gross_audio_not_uploaded: number;
  gross_audio_by_upload_mode: Record<string, number>;
  gross_audio_with_upload_error: number;
  gross_notes_by_type: Record<string, number>;
  audio_note_attachment_breakdown: TAudioNoteAttachmentBreakdown;
  series: Record<TTransformsUsageGranularity, TAudioNotesSeriesBucket[]>;
};

export type TAudioNotesUsageMetricsResponse =
  TAudioNotesUsageMetricsBaseResponse & {
    clinics: (TAudioNotesUsageMetricsBaseResponse & {
      clinic_uuid: string;
      clinic_name?: string | null;
    })[];
  };

type TImagesUploadedMetricsBaseResponse = {
  start_date: string;
  end_date: string;
  gross_images_by_type: Record<string, number>;
  series: Record<TTransformsUsageGranularity, TImagesUploadedSeriesBucket[]>;
};

export type TImagesUploadedUsageMetricsResponse =
  TImagesUploadedMetricsBaseResponse & {
    clinics: (TImagesUploadedMetricsBaseResponse & {
      clinic_uuid: string;
      clinic_name?: string | null;
    })[];
  };

export type TSideBySideSeriesBucket = {
  period_start: string;
  images_total: number;
  images_uploaded: number;
  images_not_uploaded: number;
};

type TSideBySideUsageMetricsBaseResponse = {
  start_date: string;
  end_date: string;
  gross_images_total: number;
  gross_images_uploaded: number;
  gross_images_not_uploaded: number;
  series: Record<TTransformsUsageGranularity, TSideBySideSeriesBucket[]>;
};

export type TSideBySideUsageMetricsResponse =
  TSideBySideUsageMetricsBaseResponse & {
    clinics: (TSideBySideUsageMetricsBaseResponse & {
      clinic_uuid: string;
      clinic_name?: string | null;
    })[];
  };

export type TImageMetadataBacklogAgeBucket = {
  label: string;
  count: number;
};

export type TImageMetadataBacklogClinic = {
  clinic_uuid: string;
  clinic_name?: string | null;
  total_unprocessed: number;
  average_age_hours?: number | null;
  oldest_created_at?: string | null;
  newest_created_at?: string | null;
  age_buckets?: TImageMetadataBacklogAgeBucket[];
};

export type TImageMetadataBacklogItem = {
  id: string;
  clinic_uuid: string;
  created_at: string;
  age_seconds: number;
};

export type TImageMetadataBacklogMetricsResponse = {
  as_of: string;
  total_unprocessed: number;
  count_under_2m: number;
  count_over_2m: number;
  average_age_hours: number;
  p50_age_seconds: number;
  p95_age_seconds: number;
  p99_age_seconds: number;
  max_age_seconds: number;
  oldest_created_at?: string | null;
  newest_created_at?: string | null;
  age_buckets: TImageMetadataBacklogAgeBucket[];
  clinics: TImageMetadataBacklogClinic[];
  items?: TImageMetadataBacklogItem[];
  backlog_items?: TImageMetadataBacklogItem[];
};

export type TAudioDebugNote = {
  id: string;
  note_type?: string | null;
  is_deleted?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
  appointment_id?: string | null;
  patient_uuid?: string | null;
  note_preview?: string | null;
};

export type TAudioDebugAudio = {
  id?: number | string | null;
  uuid?: string | null;
  clinic_uuid?: string | null;
  patient_uuid?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  date_of_recording?: string | null;
  type?: string | null;
  encounter_type?: string | null;
  is_uploaded?: boolean | null;
  is_deleted?: boolean | null;
  is_dummy_data?: boolean | null;
  duration_in_seconds?: number | null;
  upload_mode?: string | null;
  upload_status?: string | null;
  upload_error?: string | null;
  finalized_at?: string | null;
  s3_key?: string | null;
  transcription_s3_key?: string | null;
};

export type TAudioDebugUploadSession = {
  id?: number | string | null;
  status?: string | null;
  storage_strategy?: string | null;
  s3_prefix?: string | null;
  last_part_number?: number | null;
  expected_part_count?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type TAudioDebugDebug = {
  note_count?: number | null;
  has_upload_error?: boolean | null;
  has_transcription_key?: boolean | null;
  is_chunked?: boolean | null;
};

export type TAudioDebugRecord = {
  audio: TAudioDebugAudio;
  upload_session?: TAudioDebugUploadSession | null;
  debug?: TAudioDebugDebug | null;
  notes?: TAudioDebugNote[];
};

export type TAudioDebugRecordsResponse = {
  start_date?: string;
  end_date?: string;
  return_failures_only?: boolean;
  records: TAudioDebugRecord[];
};

export type TClinicActivityStreamEvent = {
  event_type: string;
  event_id: string;
  created_at: string;
  source: string;
  details: Record<string, unknown>;
};

export type TClinicActivityStreamResponse = {
  clinic_uuid: string;
  start_date: string;
  end_date: string;
  next_cursor?: string | null;
  events: TClinicActivityStreamEvent[];
};

export type TAdminClinicSignupUser = {
  clinic_user_id: number;
  supabase_uuid: string;
  role: string;
  signup_created_at: string;
  email?: string | null;
};

export type TAdminClinicSignupClinic = {
  clinic_uuid: string;
  clinic_name?: string | null;
  clinic_created_at: string;
  users: TAdminClinicSignupUser[];
};

export type TAdminClinicSignupsResponse = {
  total_clinics_matching: number;
  limit: number;
  next_cursor?: string | null;
  clinics: TAdminClinicSignupClinic[];
};

export type TBillingAdminAuth =
  | { type: "apiKey"; secret: string }
  | { type: "jwt"; secret: string };

export class BillingAdminApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "BillingAdminApiError";
    this.status = status;
  }
}

const buildHeaders = (auth: TBillingAdminAuth) => {
  const baseHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (auth.type === "apiKey") {
    return {
      ...baseHeaders,
      "X-Billing-Admin-Key": auth.secret,
    };
  }

  return {
    ...baseHeaders,
    Authorization: `Bearer ${auth.secret}`,
  };
};

const buildError = async (response: Response, auth: TBillingAdminAuth) => {
  let message = `Request failed (${response.status})`;
  const text = await response.text();
  let detail = "";
  if (text) {
    try {
      const data = JSON.parse(text);
      detail = data?.detail || "";
    } catch {
      detail = text;
    }
  }

  if (response.status === 401 || response.status === 403) {
    const authLabel =
      auth.type === "apiKey" ? "billing admin key" : "admin session";
    message =
      response.status === 401
        ? `Unauthorized – Please re-enter your ${authLabel}.`
        : `Forbidden – Your ${authLabel} is missing required permissions.`;
  } else if (response.status === 422) {
    message = detail ? `Validation error – ${detail}` : "Validation error.";
  } else if (detail) {
    message = detail;
  }

  throw new BillingAdminApiError(message, response.status);
};

type BillingAdminSummaryOptions = {
  excludeClinicNameContains?: string[];
};

export const getBillingAdminSummary = async (
  auth: TBillingAdminAuth,
  options?: BillingAdminSummaryOptions,
): Promise<TBillingAdminSummaryResponse> => {
  const params = new URLSearchParams();
  options?.excludeClinicNameContains
    ?.map((term) => term?.trim())
    .filter((term): term is string => Boolean(term))
    .forEach((term) => params.append("exclude_clinic_name_contains", term));

  const url = params.toString()
    ? `${billingAdminBaseUrl}/summary?${params.toString()}`
    : `${billingAdminBaseUrl}/summary`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TBillingAdminSummaryResponse;
};

export const getBillingAdminClinicHistory = async (
  clinicUuid: string,
  auth: TBillingAdminAuth,
): Promise<TBillingAdminClinicHistoryResponse> => {
  const response = await fetch(`${billingAdminBaseUrl}/${clinicUuid}/history`, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TBillingAdminClinicHistoryResponse;
};

type TTransformsUsageMetricsOptions = {
  startDate?: string;
  endDate?: string;
};

type TAnalyticsCommonOptions = TTransformsUsageMetricsOptions & {
  clinicId?: string;
  limit?: number;
};

export const getBillingAdminTransformsUsageMetrics = async (
  auth: TBillingAdminAuth,
  options?: TTransformsUsageMetricsOptions,
): Promise<TTransformsUsageMetricsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/transforms-usage-metrics?${params.toString()}`
    : `${analyticsBaseUrl}/transforms-usage-metrics`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TTransformsUsageMetricsResponse;
};

export const getBillingAdminTransformsUsageMetricsByProcedure = async (
  auth: TBillingAdminAuth,
  options?: TTransformsUsageMetricsOptions,
): Promise<TTransformsUsageMetricsByProcedureResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/transforms-usage-metrics-by-procedure?${params.toString()}`
    : `${analyticsBaseUrl}/transforms-usage-metrics-by-procedure`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TTransformsUsageMetricsByProcedureResponse;
};

export const getBillingAdminAudioNotesUsageMetrics = async (
  auth: TBillingAdminAuth,
  options?: TTransformsUsageMetricsOptions,
): Promise<TAudioNotesUsageMetricsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/audio-notes-usage-metrics?${params.toString()}`
    : `${analyticsBaseUrl}/audio-notes-usage-metrics`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TAudioNotesUsageMetricsResponse;
};

export const getBillingAdminImagesUploadedUsageMetrics = async (
  auth: TBillingAdminAuth,
  options?: TTransformsUsageMetricsOptions,
): Promise<TImagesUploadedUsageMetricsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/images-uploaded-usage-metrics?${params.toString()}`
    : `${analyticsBaseUrl}/images-uploaded-usage-metrics`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TImagesUploadedUsageMetricsResponse;
};

export const getBillingAdminSideBySideUsageMetrics = async (
  auth: TBillingAdminAuth,
  options?: TAnalyticsCommonOptions,
): Promise<TSideBySideUsageMetricsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }
  if (options?.clinicId?.trim()) {
    params.set("clinic_id", options.clinicId.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/side-by-side-usage-metrics?${params.toString()}`
    : `${analyticsBaseUrl}/side-by-side-usage-metrics`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TSideBySideUsageMetricsResponse;
};

export const getBillingAdminImageMetadataBacklogMetrics = async (
  auth: TBillingAdminAuth,
  options?: TAnalyticsCommonOptions,
): Promise<TImageMetadataBacklogMetricsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }
  if (options?.clinicId?.trim()) {
    params.set("clinic_id", options.clinicId.trim());
  }
  if (typeof options?.limit === "number") {
    params.set("limit", String(options.limit));
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/image-metadata-backlog-metrics?${params.toString()}`
    : `${analyticsBaseUrl}/image-metadata-backlog-metrics`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TImageMetadataBacklogMetricsResponse;
};

type TAudioDebugRecordsOptions = TTransformsUsageMetricsOptions & {
  clinicId?: string;
  returnFailuresOnly?: boolean;
  limit?: number;
};

export const getBillingAdminAudioDebugRecords = async (
  auth: TBillingAdminAuth,
  options?: TAudioDebugRecordsOptions,
): Promise<TAudioDebugRecordsResponse> => {
  const params = new URLSearchParams();

  if (options?.clinicId?.trim()) {
    params.set("clinic_id", options.clinicId.trim());
  }
  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }
  if (typeof options?.returnFailuresOnly === "boolean") {
    params.set("return_failures_only", String(options.returnFailuresOnly));
  }
  if (typeof options?.limit === "number") {
    params.set("limit", String(options.limit));
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/audio-debug-records?${params.toString()}`
    : `${analyticsBaseUrl}/audio-debug-records`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TAudioDebugRecordsResponse;
};

type TClinicActivityStreamOptions = {
  clinicId: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  cursor?: string;
};

export const getBillingAdminClinicActivityStream = async (
  auth: TBillingAdminAuth,
  options: TClinicActivityStreamOptions,
): Promise<TClinicActivityStreamResponse> => {
  const params = new URLSearchParams();
  params.set("clinic_id", options.clinicId);

  if (options.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }
  if (typeof options.limit === "number") {
    params.set("limit", String(options.limit));
  }
  if (options.cursor?.trim()) {
    params.set("cursor", options.cursor.trim());
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = `${analyticsBaseUrl}/clinic-activity-stream?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TClinicActivityStreamResponse;
};

type TAdminClinicSignupsOptions = {
  startDate?: string;
  endDate?: string;
  limit?: number;
  cursor?: string;
  includeDemo?: boolean;
};

export const getBillingAdminClinicSignups = async (
  auth: TBillingAdminAuth,
  options?: TAdminClinicSignupsOptions,
): Promise<TAdminClinicSignupsResponse> => {
  const params = new URLSearchParams();

  if (options?.startDate?.trim()) {
    params.set("start_date", options.startDate.trim());
  }
  if (options?.endDate?.trim()) {
    params.set("end_date", options.endDate.trim());
  }
  if (typeof options?.limit === "number") {
    params.set("limit", String(options.limit));
  }
  if (options?.cursor?.trim()) {
    params.set("cursor", options.cursor.trim());
  }
  if (typeof options?.includeDemo === "boolean") {
    params.set("include_demo", String(options.includeDemo));
  }

  const analyticsBaseUrl = `${protocol}://${host}/clinics/v1/analytics/admin`;
  const url = params.toString()
    ? `${analyticsBaseUrl}/clinic-signups?${params.toString()}`
    : `${analyticsBaseUrl}/clinic-signups`;

  const response = await fetch(url, {
    method: "GET",
    headers: buildHeaders(auth),
  });

  if (!response.ok) {
    await buildError(response, auth);
  }

  return (await response.json()) as TAdminClinicSignupsResponse;
};
